import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { clearSessionState } from '@/lib/auth/clearSessionState';
import { decodeJwt } from '@/lib/auth/tokenDecode';

type ImpersonationState = {
  fanEmail: string | null;
  impersonatedBy: string;
};

// gloss
const decodeImpersonation = (token: string | null): ImpersonationState | null => {
  if (!token) return null;

  try {
    const payload = decodeJwt(token);
    if (!payload.impersonatedBy) return null;
    return {
      fanEmail: typeof payload.email === 'string' ? payload.email : null,
      impersonatedBy: payload.impersonatedBy,
    };
  } catch {
    return null;
  }
};

// gloss
export const ImpersonationBanner = () => {
  const router = useRouter();
  const { token } = useAuthToken();
  const state = useMemo(() => decodeImpersonation(token), [token]);

  if (!state) return null;

  const handleStop = () => {
    // why: clearMagicToken alone leaves resolver caches + currentUser slice holding the
    // why: impersonated fan — must clear all session state.
    clearSessionState();
    void router.replace('/auth/impersonation-ended');
  };

  return (
    <div
      role="status"
      className="sticky top-0 z-1000 flex items-center justify-between gap-3 bg-yellow-400 px-4 py-2.5 text-sm text-black shadow-xs"
    >
      <span>
        You are impersonating <strong>{state.fanEmail ?? 'a fan'}</strong>. All actions are logged.
      </span>
      <button type="button" onClick={handleStop} className="rounded bg-black px-3 py-1.5 font-semibold text-yellow-400">
        Stop impersonating
      </button>
    </div>
  );
};
